import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

function WorklogDialog({ open, onClose, employee, token }) {
    const [worklogs, setWorklogs] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [editingLog, setEditingLog] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (open && employee) {
            fetchWorklogs();
            fetchSchedules();
        }
    }, [open, employee]);

    const fetchWorklogs = async () => {
        try {
            const response = await fetch(
                `${API_URL}/api/employees/${employee.id}/worklogs`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            const data = await response.json();
            if (data.success) {
                setWorklogs(data.worklogs);
            }
        } catch (error) {
            console.error('Error fetching worklogs:', error);
        }
    };

    const fetchSchedules = async () => {
        try {
            const response = await fetch(
                `${API_URL}/api/employees/${employee.id}/schedule`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            const data = await response.json();
            if (data.success) {
                // Только смены со статусом "scheduled"
                setSchedules(data.schedules.filter(s => s.status === 'scheduled'));
            }
        } catch (error) {
            console.error('Error fetching schedules:', error);
        }
    };

    const calculateHours = (start, end) => {
        if (!start || !end) return 0;
        const [startH, startM] = start.split(':').map(Number);
        const [endH, endM] = end.split(':').map(Number);
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        return ((endMinutes - startMinutes) / 60).toFixed(2);
    };

    const handleStartEdit = (schedule) => {
        setEditingLog({
            scheduleId: schedule.id,
            workDate: schedule.work_date,
            eventId: schedule.event_id,
            actualStart: schedule.start_time,
            actualEnd: schedule.end_time,
            notes: ''
        });
    };

    const handleSaveWorklog = async () => {
        setError('');
        setSuccess('');

        if (!editingLog.actualStart || !editingLog.actualEnd) {
            setError('Заполните время начала и окончания');
            return;
        }

        try {
            const actualHours = calculateHours(editingLog.actualStart, editingLog.actualEnd);

            const response = await fetch(
                `${API_URL}/api/employees/${employee.id}/worklogs`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        scheduleId: editingLog.scheduleId,
                        workDate: editingLog.workDate,
                        eventId: editingLog.eventId || null,
                        actualStart: editingLog.actualStart,
                        actualEnd: editingLog.actualEnd,
                        actualHours: parseFloat(actualHours),
                        notes: editingLog.notes
                    })
                }
            );

            const data = await response.json();

            if (data.success) {
                setSuccess('Часы сохранены');
                setEditingLog(null);
                fetchWorklogs();
                fetchSchedules();
            } else {
                setError(data.error || 'Ошибка сохранения');
            }
        } catch (error) {
            setError('Ошибка подключения к серверу');
        }
    };

    const totalPlannedHours = schedules.reduce((sum, s) => sum + parseFloat(s.hours || 0), 0);
    const totalWorkedHours = worklogs.reduce((sum, w) => sum + parseFloat(w.actual_hours || 0), 0);
    const totalEarnings = (totalWorkedHours * (employee?.hourly_rate || 0)).toFixed(2);

    if (!employee) {
        return null;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                        <AccessTimeIcon color="primary" />
                        <Typography variant="h6">
                            Учет рабочего времени: {employee?.full_name}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {/* Статистика */}
                <Box display="flex" gap={3} mb={3}>
                    <Paper sx={{ p: 2, flex: 1, bgcolor: '#e3f2fd' }}>
                        <Typography variant="caption" color="textSecondary">Запланировано часов</Typography>
                        <Typography variant="h5">{totalPlannedHours.toFixed(2)} ч</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, flex: 1, bgcolor: '#e8f5e9' }}>
                        <Typography variant="caption" color="textSecondary">Отработано часов</Typography>
                        <Typography variant="h5">{totalWorkedHours.toFixed(2)} ч</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, flex: 1, bgcolor: '#fff3e0' }}>
                        <Typography variant="caption" color="textSecondary">К выплате (₽)</Typography>
                        <Typography variant="h5">{totalEarnings} ₽</Typography>
                    </Paper>
                </Box>

                {/* Смены для отметки */}
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    Запланированные смены (требуют отметки)
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Дата</TableCell>
                                <TableCell>План время</TableCell>
                                <TableCell>План часов</TableCell>
                                <TableCell>Действие</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {schedules.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        Нет смен для отметки
                                    </TableCell>
                                </TableRow>
                            ) : (
                                schedules.map((schedule) => (
                                    <TableRow key={schedule.id}>
                                        <TableCell>
                                            {new Date(schedule.work_date).toLocaleDateString('ru-RU')}
                                        </TableCell>
                                        <TableCell>
                                            {schedule.start_time} - {schedule.end_time}
                                        </TableCell>
                                        <TableCell>{schedule.hours} ч</TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleStartEdit(schedule)}
                                            >
                                                Отметить
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Форма отметки времени */}
                {editingLog && (
                    <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                            Отметка смены за {new Date(editingLog.workDate).toLocaleDateString('ru-RU')}
                        </Typography>
                        <Box display="flex" gap={2} mb={2}>
                            <TextField
                                type="time"
                                label="Фактическое начало"
                                value={editingLog.actualStart}
                                onChange={(e) => setEditingLog({ ...editingLog, actualStart: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                type="time"
                                label="Фактическое окончание"
                                value={editingLog.actualEnd}
                                onChange={(e) => setEditingLog({ ...editingLog, actualEnd: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Часов"
                                value={calculateHours(editingLog.actualStart, editingLog.actualEnd)}
                                InputProps={{ readOnly: true }}
                            />
                        </Box>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Примечания"
                            value={editingLog.notes}
                            onChange={(e) => setEditingLog({ ...editingLog, notes: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <Box display="flex" gap={1}>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveWorklog}
                            >
                                Сохранить
                            </Button>
                            <Button onClick={() => setEditingLog(null)}>
                                Отмена
                            </Button>
                        </Box>
                    </Paper>
                )}

                {/* История отработанных смен */}
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    История отработанного времени
                </Typography>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Дата</TableCell>
                                <TableCell>Время</TableCell>
                                <TableCell>Часов</TableCell>
                                <TableCell>Заработано</TableCell>
                                <TableCell>Примечания</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {worklogs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Нет записей
                                    </TableCell>
                                </TableRow>
                            ) : (
                                worklogs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell>
                                            {new Date(log.work_date).toLocaleDateString('ru-RU')}
                                        </TableCell>
                                        <TableCell>
                                            {log.actual_start} - {log.actual_end}
                                        </TableCell>
                                        <TableCell>{log.actual_hours} ч</TableCell>
                                        <TableCell>
                                            {(log.actual_hours * (employee.hourly_rate || 0)).toFixed(2)} ₽
                                        </TableCell>
                                        <TableCell>{log.notes || '-'}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
}

export default WorklogDialog;
